import '../interpretation/_interpretation.style.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import {
  useGetVariantAssessmentArgumentsQuery,
  useGetVariantLinkoutsQuery,
} from '../../api/variantApi';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import DetailCard from '../components/DetailCard';
import LoadMoreBtn from '../components/LoadMoreBtn';
import SectionEditBtn from '../components/SectionEditBtn';
import { useVariantAccess } from '../hooks/useVariantAccess';
import ArgumentsClassificationModal from './ArgumentsClassificationModal';
import ClassificationTitle from './ClassificationTitle';
import {
  handleRowProps,
  useArgumentsTableColumns,
} from './helpers/argumentsHelpers';
import {
  structureArgumentsData,
  StructuredArgument,
} from './helpers/structureArgumentsData';

type ClassificationSectionProps = {
  forceExpanded?: boolean;
};

const ClassificationSection = ({
  forceExpanded = false,
}: ClassificationSectionProps) => {
  const params = useParams();

  const [showMore, setShowMore] = useState(false);
  const [isClassificationModalOpen, setIsClassificationModalOpen] =
    useState(false);
  const { canEditWorkflow } = useVariantAccess();

  const [dataSource, setDataSource] = useState<StructuredArgument[]>([]);
  const isExpanded = forceExpanded || showMore;

  const { data: variantArgumentsForClassification } =
    useGetVariantAssessmentArgumentsQuery(
      { variant_id: params.vid },
      { skip: !params.vid },
    );

  const tableDataSource = useMemo(() => {
    return structureArgumentsData(
      variantArgumentsForClassification ?? [],
      false,
    );
  }, [variantArgumentsForClassification]);

  const columns = useArgumentsTableColumns(
    false,
    isExpanded,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {},
  );

  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  useEffect(() => {
    setDataSource(
      structureArgumentsData(variantArgumentsForClassification ?? [], true),
    );
  }, [variantArgumentsForClassification]);

  return (
    <DetailCard>
      {isClassificationModalOpen && (
        <ArgumentsClassificationModal
          closeModal={() => setIsClassificationModalOpen(false)}
          dataSource={dataSource}
          onRow={handleRowProps}
          variantId={params.vid as string}
        />
      )}
      <div className="l-interpretation__collapse l-interpretation__collapse-classification">
        <CollapseHeaderForSections
          title={'Arguments for classification: overview'}
        />
        <SectionEditBtn
          onClick={() => setIsClassificationModalOpen(true)}
          disabled={!canEditWorkflow}
        />
      </div>
      <div className="classification-section__content">
        {linkouts?.intervar_url && (
          <div className="interpretation__intervar-link">
            <a
              href={linkouts.intervar_url}
              target="_blank"
              rel="noopener noreferrer"
              className="interpretation__intervar-link__anchor"
            >
              InterVar
            </a>
          </div>
        )}
        <ClassificationTitle />
        <div className="interpretation__classification-details">
          <ResizableTable
            columns={columns}
            dataSource={tableDataSource}
            pagination={false}
            rowKey={(record, index) =>
              `${record.stage}-${record.section}-${index}`
            }
            className="interpretation__classification-table"
            onRow={handleRowProps}
            persistenceKey="arguments-overview"
          />
        </div>
        <LoadMoreBtn
          showMore={isExpanded}
          handleShowMoreClick={handleShowMoreClick}
        />
      </div>
    </DetailCard>
  );
};

export default ClassificationSection;
